/**
 * Back in Stock notify component.
 *
 * Lives inside the buy-buttons block on non-clearance products that have a
 * sold-out variant. It is always present in the DOM (hidden when the selected
 * variant is in stock) and listens to the theme's `variant:update` event so it
 * swaps with the Add-to-cart / Buy buttons live, without a page refresh.
 *
 * On submit it creates a Klaviyo Back-in-Stock subscription for the exact
 * Shopify variant so the customer is emailed when it is restocked.
 */
(function () {
  if (window.customElements.get('back-in-stock')) return;

  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var VARIANT_UPDATE_EVENT = 'variant:update';

  class BackInStock extends HTMLElement {
    connectedCallback() {
      if (this.__wired) return;
      this.__wired = true;

      this.trigger = this.querySelector('[data-bis-trigger]');
      this.panel = this.querySelector('[data-bis-panel]');
      this.emailInput = this.querySelector('[data-bis-email]');
      this.submitBtn = this.querySelector('[data-bis-submit]');
      this.feedback = this.querySelector('[data-bis-feedback]');
      this.submitLabel = this.submitBtn ? this.submitBtn.textContent : 'Notify me';

      if (this.trigger) this.trigger.addEventListener('click', this.toggle.bind(this));
      if (this.submitBtn) this.submitBtn.addEventListener('click', this.submit.bind(this));
      if (this.emailInput) {
        this.emailInput.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); this.submit(); }
        }.bind(this));
      }

      // Keep in sync with variant selection (no page refresh required).
      this.__controller = new AbortController();
      document.addEventListener(
        VARIANT_UPDATE_EVENT,
        this.onVariantUpdate.bind(this),
        { signal: this.__controller.signal }
      );
    }

    disconnectedCallback() {
      if (this.__controller) this.__controller.abort();
    }

    /** Find the sibling wrapper holding the default Add-to-cart / Buy buttons. */
    get defaultButtons() {
      var scope = this.closest('.product-form-buttons') || this.parentElement;
      return scope ? scope.querySelector('.buy-buttons-default') : null;
    }

    onVariantUpdate(event) {
      var detail = event.detail || {};
      // Only react to our own product's variant changes (ignore quick-add / recommendations).
      var myProductId = this.dataset.productId;
      var evtProductId = detail.data && detail.data.productId;
      if (myProductId && evtProductId && String(evtProductId) !== String(myProductId)) return;

      var resource = detail.resource;
      var soldOut = !!(resource && resource.available === false && resource.inventory_management);
      var wrap = this.defaultButtons;

      if (soldOut) {
        if (resource && resource.id != null) this.dataset.variantId = String(resource.id);
        this.resetForm();
        this.removeAttribute('hidden');
        if (wrap) wrap.classList.add('hidden');
      } else {
        this.collapse();
        this.setAttribute('hidden', '');
        if (wrap) wrap.classList.remove('hidden');
      }
    }

    collapse() {
      if (this.panel) this.panel.setAttribute('hidden', '');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    }

    /** Restore the form to a fresh state when switching to a different sold-out size. */
    resetForm() {
      this.collapse();
      this.classList.remove('is-subscribed');
      if (this.emailInput) {
        this.emailInput.disabled = false;
        this.emailInput.value = '';
      }
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        this.submitBtn.textContent = this.submitLabel;
      }
      if (this.feedback) {
        this.feedback.textContent = '';
        this.feedback.setAttribute('hidden', '');
      }
    }

    toggle() {
      if (!this.panel) return;
      var hidden = this.panel.hasAttribute('hidden');
      if (hidden) {
        this.panel.removeAttribute('hidden');
        this.trigger.setAttribute('aria-expanded', 'true');
        if (this.emailInput) this.emailInput.focus();
      } else {
        this.collapse();
      }
    }

    setFeedback(msg, ok) {
      if (!this.feedback) return;
      this.feedback.textContent = msg;
      this.feedback.classList.remove('back-in-stock__feedback--ok', 'back-in-stock__feedback--err');
      this.feedback.classList.add(ok ? 'back-in-stock__feedback--ok' : 'back-in-stock__feedback--err');
      this.feedback.removeAttribute('hidden');
    }

    submit() {
      var self = this;
      var email = (this.emailInput && this.emailInput.value || '').trim();
      var companyId = this.dataset.companyId;
      var variantId = this.dataset.variantId;

      if (!EMAIL_RE.test(email)) {
        this.setFeedback('Please enter a valid email address.', false);
        if (this.emailInput) this.emailInput.focus();
        return;
      }

      this.submitBtn.disabled = true;
      this.submitBtn.textContent = 'Submitting…';

      // Subscribe them to the single opt-in "Restock & New Arrivals" list. This grants email
      // consent and clears any suppression, so the restock alert can actually be delivered
      // (a back-in-stock request alone does NOT un-suppress a profile in Klaviyo). The visible
      // notice on the form is the customer's explicit opt-in for this.
      var listId = this.dataset.listId;
      if (listId) {
        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Back in Stock',
                profile: { data: { type: 'profile', attributes: { email: email } } }
              },
              relationships: { list: { data: { type: 'list', id: listId } } }
            }
          })
        }).catch(function () { /* non-blocking: the restock subscription below is still created */ });
      }

      fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
            }
          }
        })
      }).then(function (res) {
        if (res.ok || res.status === 202) {
          // Collapse the whole UI down to just the confirmation message.
          self.setFeedback("You're on the list — we'll email you when it's back.", true);
          if (self.panel) self.panel.removeAttribute('hidden');
          self.classList.add('is-subscribed');
          return;
        }
        return res.json().catch(function () { return null; }).then(function (body) {
          var detail = body && body.errors && body.errors[0] && body.errors[0].detail;
          self.setFeedback(detail || 'Something went wrong. Please try again.', false);
          self.submitBtn.disabled = false;
          self.submitBtn.textContent = self.submitLabel;
        });
      }).catch(function () {
        self.setFeedback('Network error. Please try again.', false);
        self.submitBtn.disabled = false;
        self.submitBtn.textContent = self.submitLabel;
      });
    }
  }

  window.customElements.define('back-in-stock', BackInStock);
})();
